"use client";

import { useQuery } from "@tanstack/react-query";

import { useMemo } from "react";

import type { CarePlanDetail } from "@/types/care-plan";

import { CarePlanContext } from "./care-plan";
import {
  type CarePlanSize,
  type DetailMode,
  type Orientation,
  toPatientSteps,
} from "./types";
import { useCarePlanContextValue } from "./use-care-plan-context-value";
import { useCarePlanMutations } from "./use-care-plan-mutations";
import { usePatientCarePlan } from "./use-patient-care-plan";
import { carePlanQueryOptions } from "@/api/provider/patient/care-plan-query";

interface PatientCarePlanViewerProps {
  carePlanId: string;
  patientId: string;
  orientation?: Orientation;
  detailMode?: DetailMode;
  size?: CarePlanSize;
  children: React.ReactNode;
}

/**
 * Read-only provider for viewing an existing patient care plan.
 * Fetches the care plan, derives steps + cycles, and internalizes
 * all mutations (branch selection, scheduling).
 */
export function PatientCarePlanViewer({
  carePlanId,
  patientId,
  orientation = "horizontal",
  detailMode = "popover",
  size = "md",
  children,
}: PatientCarePlanViewerProps) {
  const { data: carePlan } = useQuery(carePlanQueryOptions.detail(carePlanId));
  const { selectBranch, scheduleStep } = useCarePlanMutations(
    carePlanId,
    patientId,
  );

  return carePlan ? (
    <PatientCarePlanViewerInner
      carePlan={carePlan}
      patientId={patientId}
      orientation={orientation}
      detailMode={detailMode}
      size={size}
      onSelectBranch={selectBranch}
      onScheduleStep={scheduleStep}
    >
      {children}
    </PatientCarePlanViewerInner>
  ) : null;
}

function PatientCarePlanViewerInner({
  carePlan,
  patientId,
  orientation,
  detailMode,
  size,
  onSelectBranch,
  onScheduleStep,
  children,
}: {
  carePlan: CarePlanDetail;
  patientId: string;
  orientation: Orientation;
  detailMode: DetailMode;
  size: CarePlanSize;
  onSelectBranch: (decisionStepId: string, branchId: string) => Promise<void>;
  onScheduleStep: (step: import("./types").CarePlanStep) => void;
  children: React.ReactNode;
}) {
  const steps = useMemo(() => toPatientSteps(carePlan.steps), [carePlan]);

  const {
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    ...actions
  } = usePatientCarePlan(steps, undefined, undefined);

  const resolvedActions = { ...actions, selectBranch: onSelectBranch };

  const value = useCarePlanContextValue({
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    actions: resolvedActions,
    orientation,
    detailMode,
    readOnly: true,
    isCreating: false,
    enableMessages: true,
    size,
    onScheduleStep,
    cycles: carePlan.cycles,
    patientId,
  });

  return (
    <CarePlanContext.Provider value={value}>
      {children}
    </CarePlanContext.Provider>
  );
}
